<template>
  <input type="hidden" name="discounts" :value="discountsJWT">
  <input type="hidden" name="discounts-total" :value="totalCredit">
  <span v-if="promptMessage" class="discount-calculate-error">
    <MessagePrompt :prompt-text="promptMessage" :is-error="promptTypeError" />
  </span>
  <div v-else-if="creditsLoading" class="tmf-dynamic-price-info loading">
    <i class="fa fa-spinner fa-spin fa-3x" aria-hidden="true"></i>
    <p>Loading Price</p>
  </div>
  <div v-else class="tmf-dynamic-price-info">
    <div class="tmf-dynamic-price-info-content" :class="displayDiscounts ? 'display-discounts':'hide-discounts'">
      <div class="tmf-term-credit-info">
        <dl class="bundle-original-term-info">
          <dt class="term-length">{{ termLength }}</dt>
          <dt class="term-price">{{ formatMoney(productPrice) }}</dt>
        </dl>
        <SubscriptionCredits v-if="displayDiscounts" :products="this.products" :total-credits="totalCredit"/>
      </div>
      <ProductPrice v-if="displayDiscounts" :product-price="this.offerTotal"/>
    </div>
  </div>
</template>

<script>
import SubscriptionCredits from './SubscriptionCredits.vue'
import MessagePrompt from '@/components/MessagePrompt'
import ProductPrice from '@/components/ProductPrice'
import formatMoneyMixin from '@/mixins/formatMoneyMixin'
import useBundleStore from '@/store/bundle'
import { mapState, mapWritableState } from 'pinia'

export default {
  name: "DynamicPriceInfo",
  components: {
    MessagePrompt,
    SubscriptionCredits,
    ProductPrice
  },
  props: {
    termLength: {
      type: String,
      default: ''
    },
    offerId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      products: [],
      loggedIn: this.bundleData.loggedIn ?? false,
      discountsJWT: ''
    }
  },
  created () {
    window.addEventListener('tax-calculated', this.handleTaxCalcEvent)
  },
  computed: {
    ...mapWritableState(useBundleStore, ['discount', 'creditsLoading', 'selectedOffer']),
    ...mapState(useBundleStore, ['getOfferByID', 'total', 'getOfferTotalByID', 'setSelectedOffer', 'hasError']),
    productPrice () {
      return this.getOfferByID(this.offerId)?.productPrice
    },
    offerTotal () {
      return this.getOfferTotalByID(this.offerId)
    },
    totalCredit () {
      return this.products.reduce((accumulator, product) => (accumulator + (product?.discount?.amount ?? 0)), 0)
    },
    displayDiscounts () {
      return (this.loggedIn)
    },
    promptTypeError () {
      return Boolean(this.hasError)
    },
    promptMessage () {
      if (this.hasError) {
        return this.bundleData.errorPrompt
      }
      if (this.offerTotal <= 0) {
        if (this.ga) {
          this.ga('send', 'event', 'Order Page', 'User Discount Above Product Price', this.productPrice)
        }
        return this.bundleData.excessCreditsPrompt
      }
      return ''
    }
  },
  watch: {
    totalCredit: function (value) {
      this.discount = value
    }
  },
  mixins: [formatMoneyMixin],
  methods: {
    async getDiscounts (endpoint, entitlementsJWT) {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 180000) // 3 minutes
      const myHeaders = new Headers()

      myHeaders.append('Content-Type', 'text/plain')
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: myHeaders,
          body: entitlementsJWT,
          redirect: 'follow',
          signal: controller.signal
        })

        if (!response || !response.ok) {
          console.log('Response', response)
          throw new Error('Vindicia response was not ok')
        }

        const result = await response.text()
        const parsedResult = JSON.parse(window.atob(result.split('.')[1]))
        this.discountsJWT = result
        clearTimeout(timeoutId)
        return parsedResult
      } catch (error) {
        await this.sendSlackNotification(`Error: ${error.message}`, entitlementsJWT)
        clearTimeout(timeoutId)
        throw error
      }
    },
    handleTaxCalcEvent (event) {
      let offer = event.detail.offerId
      if (!this.creditsLoading && !this.hasError) {
        if (this.total > 0 && this.selectedOffer === offer) {
          this.commercejs.enableSubmitButton(['discount-calculation'])
        } else {
          this.commercejs.disableSubmitButton('Order Unavailable', ['too-many-credits'])
        }
      }
    },
    async sendSlackNotification (message, entitlementsJWT) {
      const parsedEntitlementsJwt = JSON.parse(window.atob(entitlementsJWT.split('.')[1]))
      const userData = {
        url: parsedEntitlementsJwt.iss,
        wpUserId: parsedEntitlementsJwt.wpUserId,
        uid: parsedEntitlementsJwt.uid,
      }
      const payload = {
        action: 'discount_api_error_slack_notification',
        security: this.bundleData.nonce,
        message: `${message}\nWP ID: ${userData.wpUserId}\nUID: ${userData.uid}\nUrl: ${userData.url}`,
      }
      const requestHeaders = new Headers()
      requestHeaders.append('Content-Type', 'application/x-www-form-urlencoded')

      await fetch(this.commerceSettings.ajaxUrl, {
        method: 'POST',
        headers: requestHeaders,
        body: new URLSearchParams(payload),
        credentials: 'same-origin',
      })
    }
  },
  mounted () {
    if (!this.displayDiscounts || this.creditsLoading) {
      return;
    }
    this.creditsLoading = true
    this.commercejs.initialize();
    this.commercejs.disableSubmitButton('Calculating Discounts...', ['discount-calculation']);
    this.getDiscounts(this.bundleData.discountEndpoint, this.bundleData.entitlementsJWT)
    .then(response => {
      this.products = response.products
      this.products.map((product) => {
        if (typeof response.discounts[product.autoBillId] !== 'undefined') {
          product.discount = response.discounts[product.autoBillId]
        } else {
          product.discount = false
        }
        return product
      })
      this.creditsLoading = false
      if (this.offerId === this.selectedOffer) {
        this.setSelectedOffer(this.offerId)
      }
      if (this.commercejs.hasTaxPreview()) {
        this.commercejs.updateTaxPreview(this.selectedOffer, false)
        return
      }
      // have to wait for the total to be set
      setTimeout(() => {
        this.commercejs.enableSubmitButton(['discount-calculation'])
        if (this.total < 0 && this.selectedOffer === this.offerId) {
          this.commercejs.disableSubmitButton("Order Unavailable", ['too-many-credits'])
        }
      }, 500)
    })
    .catch(() => {
      const bundleStore = useBundleStore()
      bundleStore.setErrorStatus(true)
      this.bundleData.error = true
      this.commercejs.disableSubmitButton('Unable to Calculate Discount', ['discount-error'])
      this.creditsLoading = false
    })
  }
}
</script>

<style lang="scss" scoped>
@import '../assets/styles/mixins.scss';
@import '../assets/styles/variables.scss';
.discount-calculate-error {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.tmf-dynamic-price-info {
  background-color: var(--tmf-blue);
  border-top-right-radius: $border-radius;
  border-bottom-right-radius: $border-radius;
  color: #fff;
  width: 40%;
  display: flex;
}
.tmf-dynamic-price-info.loading {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 0;

  p {
    padding-top: 10px;
  }
}
.tmf-dynamic-price-info-content {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
}
.tmf-dynamic-price-info-content.hide-discounts {
  justify-content: center;
  flex-direction: row;

  .tmf-term-credit-info {
    margin: auto;
  }

  dl.bundle-original-term-info {
    flex-direction: column;
    text-align: center;
  }
}
.tmf-term-credit-info {
  padding: 2rem;
}

dl.bundle-original-term-info {
  @include line-item-style;
}

@media (max-width: $mobile-breakpoint) {
  .tmf-dynamic-price-info {
    width: 100%;
    border-top-right-radius: 0;
    border-bottom-left-radius: $border-radius;
  }
}
</style>
